import { Box, Button, Typography } from '@mui/material';
import { ExpandMore, Person, Search } from '@mui/icons-material';
import useLocalization from '../../hooks/useLocalization';
import { useModal } from '../../hooks/useDialog';
import { StyleObj } from '../../@types';
import { SelectableUserRole, USER_ROLES } from '../../constants/roles';
import UserSelectionModal from './UserSelectionModal';
import { TFunction } from 'i18next';
import { useRoles } from '../../hooks/useRoles';
import { SxProps, Theme } from '@mui/material';
import { mergeSx } from '../../utils/styles';

const getStyles = (color: string): StyleObj => ({
  button: {
    ml: 'auto',
    minWidth: 'auto',
    color,
    backgroundColor: 'transparent',
    border: 'none',
    p: 0,
    pr: 2,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '& .MuiButton-endIcon': {
      ml: 0.5,
      '& .MuiSvgIcon-root': {
        fontSize: 18,
      },
    },
  },
  selectedUser: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
  userIcon: {
    fontSize: 16,
    color,
  },
  title: {
    color,
    typography: { xs: 'body3', md: 'body2' },
  },
});

export type SelectedUser = { id: string; username: string };

type UserSelectProps = {
  selectedUser?: SelectedUser | null;
  onUserSelect: (user: SelectedUser | null) => void;
  color?: string;
  sx?: SxProps<Theme>;
  variant?: 'search' | 'select';
  title?: string;
};

const getTitleText = (userType: SelectableUserRole, t: TFunction, searchVariant: boolean) => {
  if (searchVariant) return '';
  switch (userType) {
    case USER_ROLES.MANAGER:
      return t('selectManager');
    case USER_ROLES.AGENT:
      return t('selectAgent');
    case USER_ROLES.PLAYER:
    default:
      return t('selectPlayer');
  }
};

const UserSelect = ({
  selectedUser,
  onUserSelect,
  color = 'primary.600',
  sx,
  variant = 'select',
  title,
}: UserSelectProps) => {
  const { t } = useLocalization();
  const { openModal } = useModal();
  const styles = getStyles(color);
  const isSearchVariant = variant === 'search';

  const { isAdmin, isManager, isAgent } = useRoles();

  const getUserType = () => {
    if (isAdmin) return USER_ROLES.MANAGER;
    if (isManager) return USER_ROLES.AGENT;
    if (isAgent) return USER_ROLES.PLAYER;
    return USER_ROLES.PLAYER;
  };

  const userType = getUserType();
  const titleText = title ?? getTitleText(userType, t, isSearchVariant);

  const handleOpenModal = () => {
    openModal(
      <UserSelectionModal
        selectedUserId={selectedUser?.id}
        onUserSelect={onUserSelect}
        userType={userType}
        title={titleText}
      />,
      {
        anchor: 'bottom',
        fullWidth: true,
        maxWidth: 'sm',
      }
    );
  };

  return (
    <Button
      onClick={handleOpenModal}
      endIcon={isSearchVariant ? <Search /> : <ExpandMore />}
      sx={mergeSx(styles.button, sx)}
      disableRipple
    >
      {selectedUser?.username ? (
        <Box sx={styles.selectedUser}>
          <Person sx={styles.userIcon} />
          <Typography sx={styles.title}>{selectedUser.username}</Typography>
        </Box>
      ) : (
        <Typography sx={styles.title}>{titleText}</Typography>
      )}
    </Button>
  );
};

export default UserSelect;
